import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import API from '../../utils/api';
import toast from 'react-hot-toast';
import { format, startOfMonth } from 'date-fns';
import { es } from 'date-fns/locale';

const hoy = format(new Date(), 'yyyy-MM-dd');
const num = v => parseFloat(v) || 0;
const TIPOS_BANDEJA = [2.5, 2.8, 3.5];

export default function ProduccionHelados() {
  const { puedeVer } = useAuth();
  const esGerencia = puedeVer('gerencia');
  const [tab, setTab] = useState('ordenes');

  // Tabs operativas: todos con acceso al módulo
  // Tabs de recetas: solo gerencia
  const TABS = [
    { id: 'ordenes',  label: '📋 Órdenes de producción', soloGerencia: false },
    { id: 'semis',    label: '🧴 Semielaborados',         soloGerencia: false },
    { id: 'mixes',    label: '🧪 Mixes y recetas',        soloGerencia: true  },
    { id: 'bandejas', label: '🧊 Recetas de bandejas',    soloGerencia: true  },
    { id: 'sabores',  label: '🎨 Sabores',                soloGerencia: true  },
    { id: 'cat_semi', label: '📋 Catálogo semis',         soloGerencia: true  },
  ].filter(t => !t.soloGerencia || esGerencia);

  return (
    <div style={{ fontFamily: "'Segoe UI', sans-serif", maxWidth: 1100, margin: '0 auto' }}>
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ margin: 0, fontSize: 26, fontWeight: 800, color: '#111827' }}>🍦 Producción de Helados</h1>
        <p style={{ margin: '4px 0 0', color: '#6B7280', fontSize: 14 }}>Órdenes de producción, mixes, recetas y bandejas</p>
      </div>
      <div style={{ display: 'flex', borderBottom: '2px solid #E5E7EB', marginBottom: 20 }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            padding: '10px 20px', border: 'none', cursor: 'pointer', fontSize: 13, background: 'transparent',
            fontWeight: tab === t.id ? 800 : 500,
            color: tab === t.id ? '#1a1a2e' : '#6B7280',
            borderBottom: tab === t.id ? '2px solid #1a1a2e' : '2px solid transparent',
            marginBottom: -2,
          }}>{t.label}</button>
        ))}
      </div>
      {tab === 'ordenes'  && <TabOrdenes esGerencia={esGerencia} />}
      {tab === 'semis'    && <TabSemielaborados />}
      {tab === 'mixes'    && esGerencia && <TabMixes />}
      {tab === 'bandejas' && esGerencia && <TabBandejaRecetas />}
      {tab === 'sabores'  && esGerencia && <TabSabores />}
      {tab === 'cat_semi' && esGerencia && <TabCatalogoSemis />}
    </div>
  );
}

// ── TAB ÓRDENES ──────────────────────────────────────────
function TabOrdenes({ esGerencia = false }) {
  const [ordenes, setOrdenes]   = useState([]);
  const [mixes, setMixes]       = useState([]);
  const [sabores, setSabores]   = useState([]);
  const [cargando, setCargando] = useState(false);
  const [desde, setDesde]       = useState(format(startOfMonth(new Date()), 'yyyy-MM-dd'));
  const [hasta, setHasta]       = useState(hoy);
  const [modal, setModal]       = useState(false);
  const [form, setForm]         = useState({ fecha: hoy, mix_id: '', kg_producidos: '', notas: '' });
  const [bandejas, setBandejas] = useState([]); // [{sabor_id, tipo_bandeja, cantidad}]
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    API.get('/produccion/mixes').then(r => setMixes(r.data)).catch(() => {});
    API.get('/produccion/sabores').then(r => setSabores(r.data)).catch(() => {});
  }, []);
  useEffect(() => { cargar(); }, [desde, hasta]);

  const cargar = () => {
    setCargando(true);
    API.get('/produccion/ordenes', { params: { desde, hasta } })
      .then(r => setOrdenes(r.data))
      .catch(() => toast.error('Error cargando órdenes'))
      .finally(() => setCargando(false));
  };

  const abrirModal = () => {
    setForm({ fecha: hoy, mix_id: '', kg_producidos: '', notas: '' });
    setBandejas([]);
    setModal(true);
  };

  const addBandeja = () => {
    setBandejas(b => [...b, { sabor_id: '', tipo_bandeja: 2.5, cantidad: '' }]);
  };

  const updateBandeja = (i, field, val) => {
    setBandejas(b => b.map((row, idx) => idx === i ? { ...row, [field]: val } : row));
  };

  const removeBandeja = (i) => {
    setBandejas(b => b.filter((_, idx) => idx !== i));
  };

  // Calcular kg en bandejas y merma
  const kgEnBandejas = bandejas.reduce((t, b) => t + (num(b.tipo_bandeja) * num(b.cantidad)), 0);
  const merma = num(form.kg_producidos) - kgEnBandejas;

  const handleGuardar = async (enviar) => {
    if (!form.mix_id) { toast.error('Selecciona un mix'); return; }
    if (!form.kg_producidos || num(form.kg_producidos) <= 0) { toast.error('Ingresa los kg producidos'); return; }
    if (enviar && bandejas.length === 0) { toast.error('Agrega al menos una bandeja antes de enviar'); return; }
    if (enviar && bandejas.some(b => !b.sabor_id || !b.cantidad)) { toast.error('Completa todos los campos de bandejas'); return; }
    setGuardando(true);
    try {
      await API.post('/produccion/ordenes', { ...form, bandejas, enviar });
      toast.success(enviar ? '✅ Orden enviada — MPs descontadas' : '💾 Borrador guardado');
      setModal(false);
      cargar();
    } catch(e) { toast.error(e.response?.data?.error || 'Error guardando'); }
    finally { setGuardando(false); }
  };

  // Agrupar por fecha
  const porFecha = {};
  ordenes.forEach(o => {
    const k = o.fecha.slice(0,10);
    if (!porFecha[k]) porFecha[k] = [];
    porFecha[k].push(o);
  });

  return (
    <div>
      <div style={{ background: 'white', borderRadius: 14, padding: '14px 18px', boxShadow: '0 1px 4px rgba(0,0,0,0.07)', marginBottom: 16, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div>
          <div style={st.label}>Desde</div>
          <input type="date" value={desde} onChange={e => setDesde(e.target.value)} style={st.input} />
        </div>
        <div>
          <div style={st.label}>Hasta</div>
          <input type="date" value={hasta} onChange={e => setHasta(e.target.value)} style={st.input} />
        </div>
        <button onClick={() => { setDesde(hoy); setHasta(hoy); }} style={st.btnSec}>Hoy</button>
        <button onClick={() => { setDesde(format(startOfMonth(new Date()),'yyyy-MM-dd')); setHasta(hoy); }} style={st.btnSec}>Este mes</button>
        <button onClick={abrirModal} style={{ ...st.btnPrimary, marginLeft: 'auto' }}>➕ Nueva orden</button>
      </div>

      {cargando ? (
        <div style={{ padding: 40, textAlign: 'center', color: '#9CA3AF' }}>⏳ Cargando...</div>
      ) : Object.keys(porFecha).length === 0 ? (
        <div style={st.empty}>Sin órdenes en este período</div>
      ) : Object.keys(porFecha).sort().reverse().map(fecha => (
        <div key={fecha} style={{ background: 'white', borderRadius: 14, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.07)', marginBottom: 14 }}>
          <div style={{ background: '#1a1a2e', padding: '10px 18px', display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ color: 'white', fontWeight: 800, fontSize: 13 }}>
              📅 {format(new Date(fecha+'T12:00'), "EEEE d 'de' MMMM yyyy", { locale: es })}
            </span>
            <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: 12 }}>{porFecha[fecha].length} orden(es)</span>
          </div>
          {porFecha[fecha].map(o => (
            <div key={o.id} style={{ padding: '14px 18px', borderTop: '1px solid #F3F4F6' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8 }}>
                <div>
                  <div style={{ fontWeight: 800, fontSize: 14, color: '#111827' }}>🧪 {o.mix_nombre}</div>
                  <div style={{ fontSize: 13, color: '#6B7280', marginTop: 2 }}>
                    {o.kg_producidos} kg producidos
                    {o.merma_kg > 0 && <span style={{ marginLeft: 10, color: '#EF4444', fontWeight: 600 }}>Merma: {o.merma_kg} kg</span>}
                    {o.merma_kg < 0 && <span style={{ marginLeft: 10, color: '#059669', fontWeight: 600 }}>Excedente: {Math.abs(o.merma_kg)} kg</span>}
                    {o.costo_por_kg > 0 && esGerencia && (
                      <span style={{ marginLeft: 10, color: '#2563EB', fontWeight: 600 }}>
                        ${new Intl.NumberFormat('es-CO').format(parseFloat(o.costo_por_kg).toFixed(0))}/kg
                        · Total: ${new Intl.NumberFormat('es-CO').format(parseFloat(o.costo_total).toFixed(0))}
                      </span>
                    )}
                  </div>
                  {o.notas && <div style={{ fontSize: 12, color: '#9CA3AF', marginTop: 2 }}>{o.notas}</div>}
                </div>
                <span style={{ fontSize: 11, padding: '3px 10px', borderRadius: 10, fontWeight: 700, background: o.estado === 'enviado' ? '#D1FAE5' : '#FEF3C7', color: o.estado === 'enviado' ? '#065F46' : '#92400E' }}>
                  {o.estado === 'enviado' ? '✅ Enviado' : '📝 Borrador'}
                </span>
              </div>
              {/* Bandejas */}
              {o.bandejas?.length > 0 && (
                <div style={{ marginTop: 10, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {o.bandejas.map((b, i) => (
                    <div key={i} style={{ background: '#F0F9FF', border: '1px solid #BAE6FD', borderRadius: 8, padding: '4px 10px', fontSize: 12 }}>
                      <strong>{b.sabor_nombre}</strong> · {b.tipo_bandeja}kg · <span style={{ fontWeight: 700, color: '#0369A1' }}>{b.cantidad} und</span>
                      {b.costo_unitario > 0 && esGerencia && (
                        <span style={{ marginLeft: 6, color: '#2563EB', fontSize: 11 }}>
                          · ${new Intl.NumberFormat('es-CO').format(parseFloat(b.costo_unitario).toFixed(0))}/und
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}
              <div style={{ marginTop: 6, fontSize: 11, color: '#9CA3AF' }}>
                Registrado por {o.registrado_por_nombre} · {format(new Date(o.created_at), "d MMM yyyy HH:mm", { locale: es })}
              </div>
            </div>
          ))}
        </div>
      ))}

      {/* Modal nueva orden */}
      {modal && (
        <div style={st.overlay}>
          <div style={{ ...st.modal, width: 640, maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ fontWeight: 800, fontSize: 16, marginBottom: 20 }}>📋 Nueva orden de producción</div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
              <div>
                <div style={st.label}>Fecha</div>
                <input type="date" value={form.fecha} onChange={e => setForm(f => ({ ...f, fecha: e.target.value }))} max={hoy} style={{ ...st.input, width: '100%', boxSizing: 'border-box' }} />
              </div>
              <div>
                <div style={st.label}>Mix producido</div>
                <select value={form.mix_id} onChange={e => setForm(f => ({ ...f, mix_id: e.target.value }))} style={{ ...st.input, width: '100%', boxSizing: 'border-box' }}>
                  <option value="">Seleccionar mix...</option>
                  {mixes.map(m => <option key={m.id} value={m.id}>{m.nombre}</option>)}
                </select>
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
              <div>
                <div style={st.label}>Kg producidos</div>
                <input type="number" value={form.kg_producidos} onChange={e => setForm(f => ({ ...f, kg_producidos: e.target.value }))} placeholder="0.0" style={{ ...st.input, width: '100%', boxSizing: 'border-box', fontFamily: 'monospace' }} />
              </div>
              <div>
                <div style={st.label}>Notas (opcional)</div>
                <input value={form.notas} onChange={e => setForm(f => ({ ...f, notas: e.target.value }))} placeholder="Observaciones..." style={{ ...st.input, width: '100%', boxSizing: 'border-box' }} />
              </div>
            </div>

            {/* Bandejas */}
            <div style={{ marginBottom: 14 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <div style={st.label}>Bandejas resultantes</div>
                <button onClick={addBandeja} style={{ padding: '4px 12px', border: 'none', borderRadius: 6, background: '#1a1a2e', color: 'white', cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>+ Agregar</button>
              </div>
              {bandejas.length === 0 ? (
                <div style={{ padding: '12px', background: '#F9FAFB', borderRadius: 8, textAlign: 'center', color: '#9CA3AF', fontSize: 13 }}>
                  Agrega las bandejas producidas por sabor y tipo
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {bandejas.map((b, i) => (
                    <div key={i} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr auto', gap: 8, alignItems: 'center' }}>
                      <select value={b.sabor_id} onChange={e => updateBandeja(i, 'sabor_id', e.target.value)} style={{ ...st.input, fontSize: 12 }}>
                        <option value="">Sabor...</option>
                        {sabores.map(s => <option key={s.id} value={s.id}>{s.nombre}</option>)}
                      </select>
                      <select value={b.tipo_bandeja} onChange={e => updateBandeja(i, 'tipo_bandeja', parseFloat(e.target.value))} style={{ ...st.input, fontSize: 12 }}>
                        {TIPOS_BANDEJA.map(t => <option key={t} value={t}>{t} kg</option>)}
                      </select>
                      <input type="number" value={b.cantidad} onChange={e => updateBandeja(i, 'cantidad', e.target.value)} placeholder="Cant." style={{ ...st.input, fontSize: 12, fontFamily: 'monospace' }} />
                      <button onClick={() => removeBandeja(i)} style={{ padding: '6px 10px', border: '1px solid #FCA5A5', borderRadius: 6, background: '#FEF2F2', cursor: 'pointer', color: '#DC2626', fontWeight: 700, fontSize: 12 }}>✕</button>
                    </div>
                  ))}
                </div>
              )}

              {/* Resumen kg */}
              {bandejas.length > 0 && num(form.kg_producidos) > 0 && (
                <div style={{ marginTop: 10, padding: '10px 14px', background: merma > 0.1 ? '#FEF3C7' : merma < -0.1 ? '#FEF2F2' : '#D1FAE5', borderRadius: 8, fontSize: 13 }}>
                  <span style={{ fontWeight: 700 }}>Kg en bandejas: {kgEnBandejas.toFixed(2)} kg</span>
                  {' · '}
                  <span style={{ color: Math.abs(merma) > 0.1 ? '#92400E' : '#065F46', fontWeight: 700 }}>
                    {merma > 0.1 ? `Merma: ${merma.toFixed(2)} kg` : merma < -0.1 ? `Excedente: ${Math.abs(merma).toFixed(2)} kg` : '✅ Cuadra perfecto'}
                  </span>
                </div>
              )}
            </div>

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => setModal(false)} style={st.btnCancel}>Cancelar</button>
              <button onClick={() => handleGuardar(false)} disabled={guardando} style={st.btnSec}>
                💾 Guardar borrador
              </button>
              <button onClick={() => handleGuardar(true)} disabled={guardando} style={st.btnPrimary}>
                {guardando ? '⏳...' : '✅ Enviar y descontar MPs'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── TAB MIXES ────────────────────────────────────────────
function TabMixes() {
  const [mixes, setMixes]       = useState([]);
  const [items, setItems]       = useState([]);
  const [sabores, setSabores]   = useState([]);
  const [modal, setModal]       = useState(null);
  const [form, setForm]         = useState({ nombre: '', descripcion: '' });
  const [receta, setReceta]     = useState([]);
  const [saborIds, setSaborIds] = useState([]);
  const [guardando, setGuardando] = useState(false);
  const [expandido, setExpandido] = useState({});

  const cargar = () => {
    API.get('/produccion/mixes').then(r => setMixes(r.data)).catch(() => {});
  };
  useEffect(() => {
    cargar();
    API.get('/mp/items').then(r => setItems(r.data)).catch(() => {});
    API.get('/produccion/sabores').then(r => setSabores(r.data)).catch(() => {});
  }, []);

  const abrirModal = (mix) => {
    if (mix) {
      setForm({ nombre: mix.nombre, descripcion: mix.descripcion || '' });
      setReceta(mix.receta?.map(r => ({ item_id: String(r.item_id), cantidad_por_kg: r.cantidad_por_kg, unidad: r.unidad || '' })) || []);
      setSaborIds((mix.sabores || []).map(s => String(s.sabor_id)));
    } else {
      setForm({ nombre: '', descripcion: '' });
      setReceta([]);
      setSaborIds([]);
    }
    setModal(mix || 'nuevo');
  };

  const addIngrediente = () => setReceta(r => [...r, { item_id: '', cantidad_por_kg: '', unidad: 'g' }]);
  const updateIng = (i, f, v) => setReceta(r => r.map((row, idx) => idx === i ? { ...row, [f]: v } : row));
  const removeIng = (i) => setReceta(r => r.filter((_, idx) => idx !== i));

  const toggleSabor = (id) => {
    setSaborIds(s => s.includes(String(id)) ? s.filter(x => x !== String(id)) : [...s, String(id)]);
  };

  const handleGuardar = async () => {
    if (!form.nombre.trim()) { toast.error('El nombre es obligatorio'); return; }
    setGuardando(true);
    try {
      let mixId;
      if (modal === 'nuevo') {
        const r = await API.post('/produccion/mixes', form);
        mixId = r.data.id;
      } else {
        await API.put(`/produccion/mixes/${modal.id}`, form);
        mixId = modal.id;
      }
      await API.post(`/produccion/mixes/${mixId}/receta`, {
        items: receta.filter(r => r.item_id && r.cantidad_por_kg),
        sabores: saborIds.map(Number)
      });
      toast.success('✅ Mix guardado');
      setModal(null);
      cargar();
    } catch(e) { toast.error(e.response?.data?.error || 'Error guardando'); }
    finally { setGuardando(false); }
  };

  const itemsPorCategoria = items.reduce((g, i) => { (g[i.categoria] = g[i.categoria] || []).push(i); return g; }, {});

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <button onClick={() => abrirModal(null)} style={st.btnPrimary}>➕ Nuevo mix</button>
      </div>

      {mixes.length === 0 ? (
        <div style={st.empty}>Sin mixes creados — crea el primero</div>
      ) : mixes.map(m => (
        <div key={m.id} style={{ background: 'white', borderRadius: 14, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.07)', marginBottom: 12 }}>
          <div style={{ padding: '14px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontWeight: 800, fontSize: 14 }}>🧪 {m.nombre}</div>
              {m.descripcion && <div style={{ fontSize: 12, color: '#6B7280', marginTop: 2 }}>{m.descripcion}</div>}
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
                {(m.sabores || []).map(s => (
                  <span key={s.sabor_id} style={{ fontSize: 11, background: '#EDE9FE', color: '#5B21B6', padding: '2px 8px', borderRadius: 8, fontWeight: 600 }}>{s.sabor_nombre}</span>
                ))}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => setExpandido(e => ({ ...e, [m.id]: !e[m.id] }))} style={st.btnSec}>
                {expandido[m.id] ? '▲ Ocultar' : '▼ Ver receta'}
              </button>
              <button onClick={() => abrirModal(m)} style={st.btnSec}>✏️ Editar</button>
            </div>
          </div>
          {expandido[m.id] && (
            <div style={{ borderTop: '1px solid #F3F4F6', padding: '12px 18px', background: '#FAFAFA' }}>
              {m.receta?.length === 0 ? (
                <div style={{ color: '#9CA3AF', fontSize: 13 }}>Sin ingredientes — edita para agregar</div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr>
                      {['Ingrediente', 'Cantidad por kg de mix', 'Unidad'].map(h => (
                        <th key={h} style={{ padding: '6px 10px', textAlign: h === 'Cantidad por kg de mix' ? 'right' : 'left', fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {m.receta.map((r, i) => (
                      <tr key={i} style={{ borderTop: '1px solid #F3F4F6' }}>
                        <td style={{ padding: '7px 10px', fontWeight: 600 }}>{r.mp_nombre}</td>
                        <td style={{ padding: '7px 10px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 700, color: '#059669' }}>{r.cantidad_por_kg}</td>
                        <td style={{ padding: '7px 10px', color: '#6B7280' }}>{r.unidad}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>
      ))}

      {/* Modal mix */}
      {modal !== null && (
        <div style={st.overlay}>
          <div style={{ ...st.modal, width: 680, maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ fontWeight: 800, fontSize: 16, marginBottom: 20 }}>
              {modal === 'nuevo' ? '➕ Nuevo mix' : `✏️ Editar: ${modal.nombre}`}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 16 }}>
              <div>
                <div style={st.label}>Nombre del mix</div>
                <input value={form.nombre} onChange={e => setForm(f => ({ ...f, nombre: e.target.value }))} placeholder="Ej: Mix Vainilla" autoFocus style={{ ...st.input, width: '100%', boxSizing: 'border-box' }} />
              </div>
              <div>
                <div style={st.label}>Descripción (opcional)</div>
                <input value={form.descripcion} onChange={e => setForm(f => ({ ...f, descripcion: e.target.value }))} placeholder="Notas del mix..." style={{ ...st.input, width: '100%', boxSizing: 'border-box' }} />
              </div>
            </div>

            {/* Sabores que usan este mix */}
            <div style={{ marginBottom: 16 }}>
              <div style={st.label}>Sabores que usan este mix</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
                {sabores.map(s => (
                  <button key={s.id} onClick={() => toggleSabor(s.id)} style={{
                    padding: '5px 12px', border: '2px solid', borderRadius: 20, cursor: 'pointer', fontSize: 12, fontWeight: 600,
                    borderColor: saborIds.includes(String(s.id)) ? '#5B21B6' : '#E5E7EB',
                    background: saborIds.includes(String(s.id)) ? '#EDE9FE' : 'white',
                    color: saborIds.includes(String(s.id)) ? '#5B21B6' : '#6B7280',
                  }}>{s.nombre}</button>
                ))}
              </div>
            </div>

            {/* Receta del mix */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <div style={st.label}>Ingredientes por kg de mix</div>
                <button onClick={addIngrediente} style={{ padding: '4px 12px', border: 'none', borderRadius: 6, background: '#1a1a2e', color: 'white', cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>+ Agregar</button>
              </div>
              {receta.length === 0 ? (
                <div style={{ padding: 12, background: '#F9FAFB', borderRadius: 8, textAlign: 'center', color: '#9CA3AF', fontSize: 13 }}>
                  Agrega los ingredientes de este mix
                </div>
              ) : receta.map((r, i) => (
                <div key={i} style={{ display: 'grid', gridTemplateColumns: '3fr 1fr 1fr auto', gap: 8, alignItems: 'center', marginBottom: 8 }}>
                  <select value={r.item_id} onChange={e => updateIng(i, 'item_id', e.target.value)} style={{ ...st.input, fontSize: 12 }}>
                    <option value="">Ingrediente...</option>
                    {Object.entries(itemsPorCategoria).map(([cat, its]) => (
                      <optgroup key={cat} label={cat}>
                        {its.map(it => <option key={it.id} value={it.id}>{it.nombre}</option>)}
                      </optgroup>
                    ))}
                  </select>
                  <input type="number" value={r.cantidad_por_kg} onChange={e => updateIng(i, 'cantidad_por_kg', e.target.value)} placeholder="Cant." style={{ ...st.input, fontSize: 12, fontFamily: 'monospace' }} />
                  <select value={r.unidad || 'g'} onChange={e => updateIng(i, 'unidad', e.target.value)} style={{ ...st.input, fontSize: 12 }}>
                    <option value="g">g (gramos)</option>
                    <option value="und">und (unidad)</option>
                  </select>
                  <button onClick={() => removeIng(i)} style={{ padding: '6px 10px', border: '1px solid #FCA5A5', borderRadius: 6, background: '#FEF2F2', cursor: 'pointer', color: '#DC2626', fontWeight: 700 }}>✕</button>
                </div>
              ))}
            </div>

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => setModal(null)} style={st.btnCancel}>Cancelar</button>
              <button onClick={handleGuardar} disabled={guardando} style={st.btnPrimary}>
                {guardando ? '⏳ Guardando...' : '✅ Guardar mix'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── TAB RECETAS DE BANDEJAS ──────────────────────────────
function TabBandejaRecetas() {
  const [sabores, setSabores]   = useState([]);
  const [items, setItems]       = useState([]);
  const [saborSel, setSaborSel] = useState('');
  const [tipoBandeja, setTipoBandeja] = useState(2.5);
  const [receta, setReceta]     = useState([]);
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    API.get('/produccion/sabores').then(r => setSabores(r.data)).catch(() => {});
    API.get('/mp/items').then(r => setItems(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!saborSel) { setReceta([]); return; }
    API.get('/produccion/bandeja-recetas', { params: { sabor_id: saborSel } })
      .then(r => {
        const filtrada = r.data.filter(x => parseFloat(x.tipo_bandeja) === parseFloat(tipoBandeja));
        setReceta(filtrada.map(x => ({ item_id: String(x.item_id), cantidad: x.cantidad, unidad: x.unidad || '' })));
      }).catch(() => {});
  }, [saborSel, tipoBandeja]);

  const addIng = () => setReceta(r => [...r, { item_id: '', cantidad: '', unidad: '' }]);
  const updateIng = (i, f, v) => setReceta(r => r.map((row, idx) => idx === i ? { ...row, [f]: v } : row));
  const removeIng = (i) => setReceta(r => r.filter((_, idx) => idx !== i));

  const handleGuardar = async () => {
    if (!saborSel) { toast.error('Selecciona un sabor'); return; }
    setGuardando(true);
    try {
      await API.post('/produccion/bandeja-recetas', {
        sabor_id: saborSel, tipo_bandeja: tipoBandeja,
        items: receta.filter(r => r.item_id && r.cantidad)
      });
      toast.success('✅ Receta guardada');
    } catch(e) { toast.error(e.response?.data?.error || 'Error guardando'); }
    finally { setGuardando(false); }
  };

  const itemsPorCategoria = items.reduce((g, i) => { (g[i.categoria] = g[i.categoria] || []).push(i); return g; }, {});
  const saborNombre = sabores.find(s => String(s.id) === String(saborSel))?.nombre || '';

  return (
    <div>
      <div style={{ background: 'white', borderRadius: 14, padding: '18px 20px', boxShadow: '0 1px 4px rgba(0,0,0,0.07)', marginBottom: 16 }}>
        <p style={{ margin: '0 0 14px', fontSize: 13, color: '#6B7280' }}>
          Define los ingredientes adicionales que se agregan a cada bandeja según su sabor y tamaño. El mix base se descuenta automáticamente según el kg de la bandeja.
        </p>
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 200 }}>
            <div style={st.label}>Sabor</div>
            <select value={saborSel} onChange={e => setSaborSel(e.target.value)} style={{ ...st.input, width: '100%', boxSizing: 'border-box' }}>
              <option value="">Seleccionar sabor...</option>
              {sabores.map(s => <option key={s.id} value={s.id}>{s.nombre}</option>)}
            </select>
          </div>
          <div>
            <div style={st.label}>Tipo de bandeja</div>
            <div style={{ display: 'flex', gap: 8 }}>
              {TIPOS_BANDEJA.map(t => (
                <button key={t} onClick={() => setTipoBandeja(t)} style={{
                  padding: '8px 20px', border: '2px solid', borderRadius: 10, cursor: 'pointer', fontSize: 13, fontWeight: 700,
                  borderColor: tipoBandeja === t ? '#1a1a2e' : '#E5E7EB',
                  background: tipoBandeja === t ? '#1a1a2e' : 'white',
                  color: tipoBandeja === t ? 'white' : '#6B7280',
                }}>{t} kg</button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {saborSel && (
        <div style={{ background: 'white', borderRadius: 14, padding: '18px 20px', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
            <div style={{ fontWeight: 700, fontSize: 14 }}>
              Adicionales para <span style={{ color: '#5B21B6' }}>{saborNombre}</span> — bandeja de {tipoBandeja}kg
            </div>
            <button onClick={addIng} style={{ padding: '5px 14px', border: 'none', borderRadius: 6, background: '#1a1a2e', color: 'white', cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>+ Agregar</button>
          </div>

          {receta.length === 0 ? (
            <div style={{ padding: 16, background: '#F9FAFB', borderRadius: 8, textAlign: 'center', color: '#9CA3AF', fontSize: 13 }}>
              Sin adicionales — este sabor solo usa el mix base. Agrega si tiene toppings o mezclas especiales.
            </div>
          ) : receta.map((r, i) => (
            <div key={i} style={{ display: 'grid', gridTemplateColumns: '3fr 1fr 1fr auto', gap: 8, alignItems: 'center', marginBottom: 8 }}>
              <select value={r.item_id} onChange={e => updateIng(i, 'item_id', e.target.value)} style={{ ...st.input, fontSize: 12 }}>
                <option value="">Ingrediente adicional...</option>
                {Object.entries(itemsPorCategoria).map(([cat, its]) => (
                  <optgroup key={cat} label={cat}>
                    {its.map(it => <option key={it.id} value={it.id}>{it.nombre}</option>)}
                  </optgroup>
                ))}
              </select>
              <input type="number" value={r.cantidad} onChange={e => updateIng(i, 'cantidad', e.target.value)} placeholder="Cant." style={{ ...st.input, fontSize: 12, fontFamily: 'monospace' }} />
              <input value={r.unidad} onChange={e => updateIng(i, 'unidad', e.target.value)} placeholder="Unidad" style={{ ...st.input, fontSize: 12 }} />
              <button onClick={() => removeIng(i)} style={{ padding: '6px 10px', border: '1px solid #FCA5A5', borderRadius: 6, background: '#FEF2F2', cursor: 'pointer', color: '#DC2626', fontWeight: 700 }}>✕</button>
            </div>
          ))}

          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
            <button onClick={handleGuardar} disabled={guardando} style={st.btnPrimary}>
              {guardando ? '⏳ Guardando...' : '✅ Guardar receta'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── TAB SABORES ──────────────────────────────────────────
function TabSabores() {
  const [sabores, setSabores]   = useState([]);
  const [modal, setModal]       = useState(false);
  const [form, setForm]         = useState({ nombre: '', es_premium: false });
  const [guardando, setGuardando] = useState(false);

  const cargar = () => API.get('/produccion/sabores').then(r => setSabores(r.data)).catch(() => {});
  useEffect(() => { cargar(); }, []);

  const handleGuardar = async () => {
    if (!form.nombre.trim()) { toast.error('El nombre es obligatorio'); return; }
    setGuardando(true);
    try {
      await API.post('/produccion/sabores', form);
      toast.success('✅ Sabor creado');
      setModal(false);
      setForm({ nombre: '', es_premium: false });
      cargar();
    } catch(e) { toast.error(e.response?.data?.error || 'Error guardando'); }
    finally { setGuardando(false); }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <button onClick={() => setModal(true)} style={st.btnPrimary}>➕ Nuevo sabor</button>
      </div>
      <div style={{ background: 'white', borderRadius: 14, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#F9FAFB' }}>
              {['Sabor', 'Mix asignado', 'Premium', 'Estado'].map(h => (
                <th key={h} style={{ padding: '10px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sabores.map((s, i) => (
              <tr key={s.id} style={{ borderTop: '1px solid #F3F4F6', background: i % 2 === 0 ? 'white' : '#FAFAFA' }}>
                <td style={{ padding: '10px 16px', fontWeight: 600 }}>{s.nombre}</td>
                <td style={{ padding: '10px 16px', color: s.mix_nombre ? '#5B21B6' : '#9CA3AF', fontSize: 12 }}>
                  {s.mix_nombre ? `🧪 ${s.mix_nombre}` : '— Sin asignar'}
                </td>
                <td style={{ padding: '10px 16px' }}>
                  {s.es_premium && <span style={{ fontSize: 11, background: '#FEF3C7', color: '#92400E', padding: '2px 8px', borderRadius: 8, fontWeight: 700 }}>⭐ Premium</span>}
                </td>
                <td style={{ padding: '10px 16px' }}>
                  <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 8, fontWeight: 700, background: s.activo ? '#D1FAE5' : '#FEE2E2', color: s.activo ? '#065F46' : '#991B1B' }}>
                    {s.activo ? 'Activo' : 'Inactivo'}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modal && (
        <div style={st.overlay}>
          <div style={{ ...st.modal, width: 380 }}>
            <div style={{ fontWeight: 800, fontSize: 16, marginBottom: 20 }}>🎨 Nuevo sabor</div>
            <div style={{ marginBottom: 14 }}>
              <div style={st.label}>Nombre del sabor</div>
              <input value={form.nombre} onChange={e => setForm(f => ({ ...f, nombre: e.target.value }))} placeholder="Ej: Maracuyá" autoFocus style={{ ...st.input, width: '100%', boxSizing: 'border-box' }} onKeyDown={e => e.key === 'Enter' && handleGuardar()} />
            </div>
            <div style={{ marginBottom: 20, display: 'flex', alignItems: 'center', gap: 10 }}>
              <input type="checkbox" id="premium" checked={form.es_premium} onChange={e => setForm(f => ({ ...f, es_premium: e.target.checked }))} />
              <label htmlFor="premium" style={{ fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>⭐ Es sabor premium</label>
            </div>
            <div style={{ fontSize: 12, color: '#6B7280', background: '#F9FAFB', padding: '10px 14px', borderRadius: 8, marginBottom: 16 }}>
              💡 Después de crear el sabor, ve a <strong>Mixes y recetas</strong> para asignarle su mix base, y a <strong>Recetas de bandejas</strong> para definir sus adicionales.
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => setModal(false)} style={st.btnCancel}>Cancelar</button>
              <button onClick={handleGuardar} disabled={guardando} style={st.btnPrimary}>
                {guardando ? '⏳...' : '✅ Crear sabor'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const st = {
  label: { fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4, display: 'block' },
  input: { padding: '8px 12px', border: '2px solid #E5E7EB', borderRadius: 8, fontSize: 13, outline: 'none', background: 'white' },
  btnPrimary: { padding: '9px 20px', border: 'none', borderRadius: 10, background: 'linear-gradient(135deg,#1a1a2e,#16213e)', color: 'white', cursor: 'pointer', fontSize: 13, fontWeight: 700, whiteSpace: 'nowrap' },
  btnSec: { padding: '8px 14px', border: '2px solid #E5E7EB', borderRadius: 8, background: 'white', cursor: 'pointer', fontSize: 12, fontWeight: 600, color: '#374151' },
  btnCancel: { padding: '9px 18px', border: '2px solid #E5E7EB', borderRadius: 10, background: 'white', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#6B7280' },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal: { background: 'white', borderRadius: 16, padding: 28, boxShadow: '0 20px 60px rgba(0,0,0,0.3)' },
  empty: { background: 'white', borderRadius: 14, padding: 40, textAlign: 'center', color: '#9CA3AF', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' },
};

// ── Tab Semielaborados ────────────────────────────────────
function TabSemielaborados() {
  const hoy = format(new Date(), 'yyyy-MM-dd');
  const inicioMes = format(startOfMonth(new Date()), 'yyyy-MM-dd');
  const [semis, setSemis]         = useState([]);
  const [registros, setRegistros] = useState([]);
  const [cargando, setCargando]   = useState(false);
  const [desde, setDesde]         = useState(inicioMes);
  const [hasta, setHasta]         = useState(hoy);
  const [modal, setModal]         = useState(false);
  const [editId, setEditId]       = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [form, setForm] = useState({ item_id: '', fecha: hoy, kg_entrada: '', kg_producidos: '', notas: '' });

  const COP2 = n => new Intl.NumberFormat('es-CO').format(Number(n)||0);

  useEffect(() => {
    API.get('/semi/items').then(r => setSemis(r.data)).catch(() => {});
    cargar();
  }, []);

  const cargar = () => {
    setCargando(true);
    API.get('/semi/registros', { params: { desde, hasta } })
      .then(r => setRegistros(r.data))
      .catch(() => toast.error('Error cargando registros'))
      .finally(() => setCargando(false));
  };

  const semiSel = semis.find(s => String(s.id) === String(form.item_id));
  const esFruta = semiSel?.categoria === 'Frutas procesadas';
  const mermaCalc = esFruta && form.kg_entrada && form.kg_producidos
    ? (parseFloat(form.kg_entrada) - parseFloat(form.kg_producidos)).toFixed(2) : null;
  const mermaPorc = mermaCalc && form.kg_entrada
    ? ((parseFloat(mermaCalc) / parseFloat(form.kg_entrada)) * 100).toFixed(1) : null;

  const abrirEditar = (r) => {
    setEditId(r.id);
    setForm({ item_id: String(r.item_id), fecha: r.fecha?.slice(0,10), kg_entrada: r.kg_entrada||'', kg_producidos: r.kg_producidos, notas: r.notas||'' });
    setModal(true);
  };

  const eliminar = async (r) => {
    const msg = r.estado === 'enviado'
      ? '¿Eliminar este registro enviado? Se revertirá el stock.'
      : '¿Eliminar este borrador?';
    if (!window.confirm(msg)) return;
    try {
      await API.delete(`/semi/registros/${r.id}`);
      toast.success('Registro eliminado');
      cargar();
    } catch(e) { toast.error(e.response?.data?.error || 'Error al eliminar'); }
  };

  const guardar = async (enviar) => {
    if (!form.item_id || !form.kg_producidos) { toast.error('Completa semielaborado y kg producidos'); return; }
    if (esFruta && !form.kg_entrada) { toast.error('Para frutas ingresa los kg de fruta entrada'); return; }
    setGuardando(true);
    try {
      if (editId) {
        await API.put(`/semi/registros/${editId}`, { ...form, enviar });
      } else {
        await API.post('/semi/registros', { ...form, enviar });
      }
      toast.success(enviar ? '✅ Registro enviado — stock actualizado' : 'Borrador guardado');
      setModal(false);
      setEditId(null);
      setForm({ item_id: '', fecha: hoy, kg_entrada: '', kg_producidos: '', notas: '' });
      cargar();
    } catch(e) { toast.error(e.response?.data?.error || 'Error al guardar'); }
    finally { setGuardando(false); }
  };

  const colorEstado = e => e === 'enviado' ? '#059669' : '#F59E0B';

  return (
    <div>
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div><span style={stP.label}>Desde</span><input type="date" value={desde} onChange={e => setDesde(e.target.value)} style={stP.input} /></div>
        <div><span style={stP.label}>Hasta</span><input type="date" value={hasta} onChange={e => setHasta(e.target.value)} style={stP.input} /></div>
        <button onClick={cargar} style={{ padding: '8px 16px', background: '#374151', color: 'white', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 700 }}>Buscar</button>
        <button onClick={() => setModal(true)} style={{ padding: '8px 18px', background: '#2563EB', color: 'white', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 700, marginLeft: 'auto' }}>+ Nuevo registro</button>
      </div>

      {/* Stock rápido */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
        {semis.filter(s => s.stock > 0).map(s => (
          <div key={s.id} style={{ background: 'white', borderRadius: 10, padding: '10px 14px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', minWidth: 150 }}>
            <div style={{ fontSize: 11, color: '#9CA3AF', fontWeight: 700, textTransform: 'uppercase', marginBottom: 2 }}>{s.nombre}</div>
            <div style={{ fontFamily: 'monospace', fontWeight: 800, fontSize: 16, color: '#059669' }}>{COP2(s.stock)} kg</div>
            {s.costo_promedio > 0 && <div style={{ fontSize: 11, color: '#6B7280' }}>${COP2(s.costo_promedio)}/kg</div>}
          </div>
        ))}
      </div>

      {/* Tabla registros */}
      <div style={{ background: 'white', borderRadius: 14, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#F9FAFB' }}>
              {['Fecha', 'Semielaborado', 'Entrada (kg)', 'Producido (kg)', 'Merma', 'Estado'].map(h => (
                <th key={h} style={{ padding: '10px 14px', textAlign: ['Entrada (kg)','Producido (kg)','Merma'].includes(h) ? 'right' : 'left', fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {cargando ? (
              <tr><td colSpan={6} style={{ padding: 32, textAlign: 'center', color: '#9CA3AF' }}>Cargando...</td></tr>
            ) : registros.length === 0 ? (
              <tr><td colSpan={6} style={{ padding: 32, textAlign: 'center', color: '#9CA3AF' }}>Sin registros en este período</td></tr>
            ) : registros.map((r, i) => {
              const mPct = r.merma && r.kg_entrada ? ((parseFloat(r.merma)/parseFloat(r.kg_entrada))*100).toFixed(1) : null;
              return (
                <tr key={r.id} style={{ borderTop: '1px solid #F3F4F6', background: i%2===0?'white':'#FAFAFA' }}>
                  <td style={{ padding: '10px 14px', color: '#374151' }}>{r.fecha?.slice(0,10)}</td>
                  <td style={{ padding: '10px 14px', fontWeight: 600 }}>
                    {r.item_nombre}
                    <div style={{ fontSize: 11, color: '#9CA3AF' }}>{r.categoria}</div>
                  </td>
                  <td style={{ padding: '10px 14px', textAlign: 'right', fontFamily: 'monospace' }}>{r.kg_entrada ? `${COP2(r.kg_entrada)} kg` : '—'}</td>
                  <td style={{ padding: '10px 14px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 700 }}>{COP2(r.kg_producidos)} kg</td>
                  <td style={{ padding: '10px 14px', textAlign: 'right', fontFamily: 'monospace', color: r.merma > 0 ? '#EF4444' : '#9CA3AF' }}>
                    {r.merma != null ? `${COP2(r.merma)} kg${mPct ? ` (${mPct}%)` : ''}` : '—'}
                  </td>
                  <td style={{ padding: '10px 14px' }}>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: colorEstado(r.estado) + '20', color: colorEstado(r.estado) }}>
                        {r.estado}
                      </span>
                      <button onClick={() => abrirEditar(r)} style={{ padding: '3px 10px', border: '1px solid #E5E7EB', borderRadius: 6, background: 'white', cursor: 'pointer', fontSize: 11, color: '#374151' }}>✏️</button>
                      <button onClick={() => eliminar(r)} style={{ padding: '3px 10px', border: '1px solid #FECACA', borderRadius: 6, background: 'white', cursor: 'pointer', fontSize: 11, color: '#EF4444' }}>🗑️</button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Modal nuevo registro */}
      {modal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ background: 'white', borderRadius: 16, width: '100%', maxWidth: 520 }}>
            <div style={{ background: '#1a1a2e', padding: '14px 22px', borderRadius: '16px 16px 0 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ color: 'white', fontWeight: 800 }}>{editId ? '✏️ Editar registro' : '🧴 Nuevo registro de semielaborado'}</span>
              <button onClick={() => { setModal(false); setEditId(null); setForm({ item_id: '', fecha: hoy, kg_entrada: '', kg_producidos: '', notas: '' }); }} style={{ background: 'none', border: 'none', color: 'white', fontSize: 20, cursor: 'pointer' }}>×</button>
            </div>
            <div style={{ padding: 22, display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <span style={stP.label}>Semielaborado *</span>
                <select value={form.item_id} onChange={e => setForm(f => ({...f, item_id: e.target.value}))} style={{ ...stP.input, width: '100%' }}>
                  <option value="">Seleccionar...</option>
                  {['Semielaborados','Frutas procesadas'].map(cat => (
                    <optgroup key={cat} label={cat}>
                      {semis.filter(s => s.categoria === cat).map(s => (
                        <option key={s.id} value={s.id}>{s.nombre}</option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </div>
              <div>
                <span style={stP.label}>Fecha *</span>
                <input type="date" value={form.fecha} onChange={e => setForm(f => ({...f, fecha: e.target.value}))} style={{ ...stP.input, width: '100%' }} />
              </div>
              {esFruta && (
                <div>
                  <span style={stP.label}>Kg de fruta entrada (antes de despulpar) *</span>
                  <input type="number" value={form.kg_entrada} onChange={e => setForm(f => ({...f, kg_entrada: e.target.value}))} placeholder="0" style={{ ...stP.input, width: '100%' }} />
                </div>
              )}
              <div>
                <span style={stP.label}>{esFruta ? 'Kg de pulpa obtenida *' : 'Kg producidos *'}</span>
                <input type="number" value={form.kg_producidos} onChange={e => setForm(f => ({...f, kg_producidos: e.target.value}))} placeholder="0" style={{ ...stP.input, width: '100%' }} />
              </div>
              {mermaCalc !== null && (
                <div style={{ background: parseFloat(mermaCalc) > 0 ? '#FEF2F2' : '#F0FDF4', borderRadius: 10, padding: '10px 14px' }}>
                  <span style={{ fontWeight: 700, color: parseFloat(mermaCalc) > 0 ? '#EF4444' : '#059669' }}>
                    Merma: {mermaCalc} kg ({mermaPorc}%)
                  </span>
                  <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 2 }}>
                    Peso cáscaras, semillas y pérdida del proceso
                  </div>
                </div>
              )}
              <div>
                <span style={stP.label}>Notas</span>
                <input value={form.notas} onChange={e => setForm(f => ({...f, notas: e.target.value}))} placeholder="Opcional" style={{ ...stP.input, width: '100%' }} />
              </div>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 6 }}>
                <button onClick={() => { setModal(false); setEditId(null); setForm({ item_id: '', fecha: hoy, kg_entrada: '', kg_producidos: '', notas: '' }); }} style={{ padding: '9px 18px', border: '2px solid #E5E7EB', borderRadius: 10, background: 'white', cursor: 'pointer', fontSize: 13 }}>Cancelar</button>
                <button onClick={() => guardar(false)} disabled={guardando} style={{ padding: '9px 18px', border: 'none', borderRadius: 10, background: '#F59E0B', color: 'white', cursor: 'pointer', fontSize: 13, fontWeight: 700 }}>Guardar borrador</button>
                <button onClick={() => guardar(true)} disabled={guardando} style={{ padding: '9px 18px', border: 'none', borderRadius: 10, background: '#059669', color: 'white', cursor: 'pointer', fontSize: 13, fontWeight: 700 }}>✅ Enviar</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Tab Catálogo Semielaborados ───────────────────────────
function TabCatalogoSemis() {
  const [semis, setSemis]         = useState([]);
  const [items, setItems]         = useState([]);
  const [modal, setModal]         = useState(null);
  const [receta, setReceta]       = useState([]);
  const [guardando, setGuardando] = useState(false);
  const [formNuevo, setFormNuevo] = useState({ codigo: '', nombre: '', unidad: 'kg', categoria: 'Semielaborados' });
  const [modalNuevo, setModalNuevo] = useState(false);

  useEffect(() => {
    API.get('/semi/items').then(r => setSemis(r.data)).catch(() => {});
    API.get('/mp/items').then(r => setItems(r.data)).catch(() => {});
  }, []);

  const abrirReceta = (s) => {
    setModal(s);
    setReceta(s.receta?.map(r => ({ insumo_id: String(r.insumo_id), cantidad_por_kg: r.cantidad_por_kg, unidad: r.unidad||'' })) || []);
  };

  const guardarReceta = async () => {
    if (!modal) return;
    setGuardando(true);
    try {
      await API.post(`/semi/${modal.id}/receta`, { items: receta.filter(r => r.insumo_id && r.cantidad_por_kg) });
      toast.success('Receta guardada');
      const r = await API.get('/semi/items');
      setSemis(r.data);
      setModal(null);
    } catch(e) { toast.error('Error al guardar'); }
    finally { setGuardando(false); }
  };

  const crearSemi = async () => {
    if (!formNuevo.codigo || !formNuevo.nombre) { toast.error('Código y nombre requeridos'); return; }
    try {
      await API.post('/semi/items', formNuevo);
      toast.success('Semielaborado creado');
      const r = await API.get('/semi/items');
      setSemis(r.data);
      setModalNuevo(false);
      setFormNuevo({ codigo: '', nombre: '', unidad: 'kg', categoria: 'Semielaborados' });
    } catch(e) { toast.error(e.response?.data?.error || 'Error al crear'); }
  };

  // Todos los insumos disponibles: MP + semis (para cascada)
  const todosInsumos = items.concat(semis.filter(s => !modal || s.id !== modal.id));

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <button onClick={() => setModalNuevo(true)} style={{ padding: '8px 18px', background: '#2563EB', color: 'white', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 700 }}>+ Nuevo semielaborado</button>
      </div>

      {['Semielaborados','Frutas procesadas'].map(cat => (
        <div key={cat} style={{ marginBottom: 24 }}>
          <div style={{ fontWeight: 800, fontSize: 13, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>{cat}</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px,1fr))', gap: 12 }}>
            {semis.filter(s => s.categoria === cat).map(s => (
              <div key={s.id} style={{ background: 'white', borderRadius: 12, padding: '14px 16px', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
                <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>{s.nombre}</div>
                <div style={{ fontSize: 11, color: '#9CA3AF', marginBottom: 8 }}>{s.codigo} · {s.unidad}</div>
                {s.receta?.length > 0 ? (
                  <div style={{ fontSize: 12, color: '#6B7280', marginBottom: 10 }}>
                    {s.receta.map((r, i) => (
                      <div key={i}>• {r.cantidad_por_kg} {r.unidad||'kg'} de {r.insumo_nombre} por kg</div>
                    ))}
                  </div>
                ) : (
                  <div style={{ fontSize: 12, color: '#F59E0B', marginBottom: 10 }}>⚠️ Sin receta configurada</div>
                )}
                <button onClick={() => abrirReceta(s)} style={{ padding: '6px 14px', border: '2px solid #E5E7EB', borderRadius: 8, background: 'white', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                  ✏️ Editar receta
                </button>
              </div>
            ))}
          </div>
        </div>
      ))}

      {/* Modal receta */}
      {modal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ background: 'white', borderRadius: 16, width: '100%', maxWidth: 600, maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ background: '#1a1a2e', padding: '14px 22px', borderRadius: '16px 16px 0 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ color: 'white', fontWeight: 800 }}>Receta: {modal.nombre}</span>
              <button onClick={() => setModal(null)} style={{ background: 'none', border: 'none', color: 'white', fontSize: 20, cursor: 'pointer' }}>×</button>
            </div>
            <div style={{ padding: 22 }}>
              <div style={{ fontSize: 12, color: '#6B7280', marginBottom: 14 }}>
                Ingredientes por kg de {modal.nombre} producido
              </div>
              {receta.map((r, i) => (
                <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}>
                  <select value={r.insumo_id} onChange={e => setReceta(rc => rc.map((x,j) => j===i?{...x,insumo_id:e.target.value}:x))} style={{ ...stP.input, flex: 2 }}>
                    <option value="">Seleccionar insumo...</option>
                    <optgroup label="Materias primas">
                      {items.map(it => <option key={it.id} value={it.id}>{it.nombre}</option>)}
                    </optgroup>
                    <optgroup label="Semielaborados">
                      {semis.filter(s => s.id !== modal.id).map(s => <option key={s.id} value={s.id}>{s.nombre}</option>)}
                    </optgroup>
                  </select>
                  <input type="number" value={r.cantidad_por_kg} onChange={e => setReceta(rc => rc.map((x,j) => j===i?{...x,cantidad_por_kg:e.target.value}:x))} placeholder="Cantidad/kg" style={{ ...stP.input, width: 100 }} />
                  <select value={r.unidad || 'g'} onChange={e => setReceta(rc => rc.map((x,j) => j===i?{...x,unidad:e.target.value}:x))} style={{ ...stP.input, width: 90 }}>
                    <option value="g">g</option>
                    <option value="kg">kg</option>
                    <option value="und">und</option>
                  </select>
                  <button onClick={() => setReceta(rc => rc.filter((_,j) => j!==i))} style={{ background: 'none', border: 'none', color: '#EF4444', cursor: 'pointer', fontSize: 18 }}>×</button>
                </div>
              ))}
              <button onClick={() => setReceta(r => [...r, { insumo_id: '', cantidad_por_kg: '', unidad: 'g' }])} style={{ padding: '6px 14px', border: '2px dashed #D1D5DB', borderRadius: 8, background: 'white', cursor: 'pointer', fontSize: 13, color: '#6B7280', marginBottom: 20, marginTop: 4 }}>+ Agregar ingrediente</button>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <button onClick={() => setModal(null)} style={{ padding: '9px 18px', border: '2px solid #E5E7EB', borderRadius: 10, background: 'white', cursor: 'pointer', fontSize: 13 }}>Cancelar</button>
                <button onClick={guardarReceta} disabled={guardando} style={{ padding: '9px 24px', border: 'none', borderRadius: 10, background: '#059669', color: 'white', cursor: 'pointer', fontSize: 13, fontWeight: 700 }}>💾 Guardar</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal nuevo semielaborado */}
      {modalNuevo && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ background: 'white', borderRadius: 16, width: '100%', maxWidth: 420 }}>
            <div style={{ background: '#1a1a2e', padding: '14px 22px', borderRadius: '16px 16px 0 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ color: 'white', fontWeight: 800 }}>Nuevo semielaborado</span>
              <button onClick={() => setModalNuevo(false)} style={{ background: 'none', border: 'none', color: 'white', fontSize: 20, cursor: 'pointer' }}>×</button>
            </div>
            <div style={{ padding: 22, display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div><span style={stP.label}>Código *</span><input value={formNuevo.codigo} onChange={e => setFormNuevo(f=>({...f,codigo:e.target.value}))} placeholder="ej: SEMI-009" style={{...stP.input,width:'100%'}} /></div>
              <div><span style={stP.label}>Nombre *</span><input value={formNuevo.nombre} onChange={e => setFormNuevo(f=>({...f,nombre:e.target.value}))} placeholder="Nombre del semielaborado" style={{...stP.input,width:'100%'}} /></div>
              <div><span style={stP.label}>Unidad</span>
                <select value={formNuevo.unidad} onChange={e => setFormNuevo(f=>({...f,unidad:e.target.value}))} style={{...stP.input,width:'100%'}}>
                  <option value="kg">kg</option>
                  <option value="g">g</option>
                  <option value="L">L</option>
                  <option value="und">und</option>
                </select>
              </div>
              <div><span style={stP.label}>Categoría</span>
                <select value={formNuevo.categoria} onChange={e => setFormNuevo(f=>({...f,categoria:e.target.value}))} style={{...stP.input,width:'100%'}}>
                  <option value="Semielaborados">Semielaborados</option>
                  <option value="Frutas procesadas">Frutas procesadas</option>
                </select>
              </div>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 6 }}>
                <button onClick={() => setModalNuevo(false)} style={{ padding: '9px 18px', border: '2px solid #E5E7EB', borderRadius: 10, background: 'white', cursor: 'pointer', fontSize: 13 }}>Cancelar</button>
                <button onClick={crearSemi} style={{ padding: '9px 24px', border: 'none', borderRadius: 10, background: '#2563EB', color: 'white', cursor: 'pointer', fontSize: 13, fontWeight: 700 }}>Crear</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const stP = {
  label: { fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4, display: 'block' },
  input: { padding: '8px 12px', border: '2px solid #E5E7EB', borderRadius: 8, fontSize: 13, outline: 'none', background: 'white' },
};
